import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLayoutViewport } from '../layout/LayoutViewportContext';

const AppOverlayContext = createContext(null);

const overlayPriorityRank = {
  low: 1,
  medium: 2,
  high: 3,
};

function normalizePriority(priority) {
  return overlayPriorityRank[priority] ? priority : 'medium';
}

export function AppOverlayProvider({ children }) {
  const [activeOverlay, setActiveOverlay] = useState(null);
  const { isMobile } = useLayoutViewport();
  const location = useLocation();

  const openOverlay = useCallback((config) => {
    if (!config?.key || !config?.desktopPresentation || !config?.content) {
      return;
    }

    setActiveOverlay((currentOverlay) => {
      const nextOverlay = {
        ...config,
        priority: normalizePriority(config.priority),
        closeOnRouteChange: Boolean(config.closeOnRouteChange),
      };

      if (!currentOverlay) {
        return nextOverlay;
      }

      return nextOverlay;
    });
  }, []);

  const closeOverlay = useCallback(() => {
    setActiveOverlay(null);
  }, []);

  useEffect(() => {
    setActiveOverlay((currentOverlay) => {
      if (!currentOverlay?.closeOnRouteChange) {
        return currentOverlay;
      }

      return null;
    });
  }, [location.pathname]);

  const value = useMemo(() => {
    const isOverlayOpen = Boolean(activeOverlay);
    const isFabBlocked = (isMobile && isOverlayOpen) || Boolean(activeOverlay?.blocksFab);

    return {
      activeOverlay,
      openOverlay,
      closeOverlay,
      isOverlayOpen,
      isFabBlocked,
    };
  }, [activeOverlay, closeOverlay, isMobile, openOverlay]);

  return (
    <AppOverlayContext.Provider value={value}>
      {children}
    </AppOverlayContext.Provider>
  );
}

export function useAppOverlay() {
  const context = useContext(AppOverlayContext);

  if (!context) {
    throw new Error('useAppOverlay must be used within AppOverlayProvider');
  }

  return context;
}

export { overlayPriorityRank };
